<template>
  <v-dialog v-model="model" width="800" persistent>
    <v-card style="border-radius: 10px">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h6 font-weight-bold">Record Video Introduction</span>
        <v-btn icon size="small" @click="cancelRecording">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div class="text-body-2 mb-4">
          Please keep in mind the following instructions:
        </div>
        <ul class="text-body-2 mb-4 pl-4" style="list-style-type: none">
          <li>1. Please be in a noise-free environment.</li>
          <li>2. Appear presentable and professional.</li>
          <li>3. Look into the camera when talking.</li>
          <li>4. Speak clearly and make sure you can be heard.</li>
        </ul>

        <div
          class="d-flex justify-center align-center mt-4"
          style="background: #f1f1f1; border-radius: 8px; position: relative; height: 400px;"
        >
          <!-- If a recorded video URL exists and not currently recording, show that -->
          <video
            v-if="videoUrl && !isRecording"
            :src="videoUrl"
            controls
            style="max-width: 100%; max-height: 100%;"
          ></video>

          <!-- If recording, show live preview and “Stop Recording” button -->
          <div v-else-if="isRecording" class="d-flex flex-column align-center">
            <video ref="previewVideo" autoplay muted style="max-width: 100%; max-height: 100%;"></video>
            <div class="mt-2 text-caption text-grey-darken-1">
              Recording…
            </div>
            <v-btn color="red" dark @click="stopRecording">Stop Recording</v-btn>
          </div>

          <!-- Otherwise, show “Start Recording” button -->
          <div v-else>
            <v-btn color="red" dark @click="startRecording">
              Start Recording
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="d-flex justify-end">
        <v-btn variant="outlined" @click="cancelRecording" :disabled="isRecording">
          Cancel
        </v-btn>
        <v-btn color="primary" :disabled="!recordedBlob" @click="saveVideo">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from "vue";
import { useStore } from "vuex";

const props = defineProps({ modelValue: Boolean });
const emit = defineEmits(["update:modelValue"]);
const store = useStore();

const model = ref(props.modelValue);
watch(() => props.modelValue, (val) => (model.value = val));
watch(model, (val) => emit("update:modelValue", val));

let mediaRecorder = null;
let mediaStream = null;
let videoChunks = [];
const previewVideo = ref(null);

const isRecording = ref(false);
const recordedBlob = ref(null);
const videoUrl = ref("");

async function startRecording() {
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    if (previewVideo.value) {
      previewVideo.value.srcObject = mediaStream;
      previewVideo.value.play();
    }

    mediaRecorder = new MediaRecorder(mediaStream, { mimeType: "video/webm" });
    mediaRecorder.ondataavailable = (e) => {
      videoChunks.push(e.data);
    };
    mediaRecorder.onstop = () => {
      mediaStream.getTracks().forEach((track) => track.stop());
      const blob = new Blob(videoChunks, { type: "video/webm" });
      recordedBlob.value = blob;
      videoUrl.value = URL.createObjectURL(blob);
      videoChunks = [];
      isRecording.value = false;
    };
    mediaRecorder.start();
    isRecording.value = true;
    recordedBlob.value = null;
    videoUrl.value = "";
  } catch (err) {
    console.error("⛔️ Could not start video recording:", err);
  }
}

function stopRecording() {
  if (mediaRecorder && isRecording.value) {
    mediaRecorder.stop();
  }
}

function cancelRecording() {
  if (mediaRecorder && isRecording.value) {
    mediaRecorder.stop();
  }
  resetAll();
  close();
}

function close() {
  model.value = false;
}

// Save the recorded video Blob
async function saveVideo() {
  if (!recordedBlob.value) return;

  const blob = recordedBlob.value;
  const filename = `video_${Date.now()}.webm`;

  // 1) Store Blob in Vuex (in-memory)
  store.commit("profile/SET_VIDEO_BLOB", blob);

  // 2) Store metadata + blob in IndexedDB via action
  await store.dispatch("profile/saveVideoIntro", {
    blob,
    filename,
  });

  store.dispatch("alert/success", "Video introduction saved");
  close();
}

function resetAll() {
  isRecording.value = false;
  recordedBlob.value = null;
  videoUrl.value = "";
  if (previewVideo.value) {
    previewVideo.value.srcObject = null;
  }
}

onBeforeUnmount(() => {
  if (mediaStream) {
    mediaStream.getTracks().forEach((t) => t.stop());
  }
  if (mediaRecorder && isRecording.value) {
    mediaRecorder.stop();
  }
});

// On modal open, the parent’s loadFromLocalStorage() has already rehydrated videoBlob
watch(
  () => model.value,
  (isOpen) => {
    if (isOpen) {
      // Nothing special here: if videoMeta exists, videoBlob should be in memory
      resetAll();
    }
  }
);
</script>

<style scoped>
.text-caption {
  color: rgba(0, 0, 0, 0.6);
}
</style>
